import {
  Box,
  CircularProgress,
  MenuItem,
  Modal,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import { type GridRowId, type GridSelectionModel } from "@mui/x-data-grid-pro";
import React, { useEffect, useState } from "react";
import { PageTitle } from "@/components/Title";
import { GetMonitoringAsFileAPI } from "../api";
import {
  type AxiosResponse,
  HttpStatusCode,
  isAxiosError,
  type AxiosInstance,
} from "axios";
import { MonitoringResultDataGrid } from "../components/MonitoringResultDataGrid";
import { useNavigate } from "react-router-dom";
import {
  type GetMonitoringDetailsAPIResponse,
  type MonitoringDetailEntity,
  type KeyName,
  type MonitoringResultDataGridRow,
} from "../types";
import { toast } from "react-toastify";
import { GetPrefecturesAPI } from "@/api";
import { type IPrefecture } from "@/types/prefectures";
import { type DialogProps } from "@mui/material/Dialog/Dialog";
import { SendToTrackingService } from "@/api/handlers/errors";
import { Label } from "@/components/Label";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  AcquireMultipleBooksModal,
  getBookTypeFromRealEstateTypeLabel,
} from "@/features/map/components/AcquireMultipleBooksModal";
import { useAcquireBookForm } from "@/features/acquireMultipleBooks/hooks/useAcquireBookForm";
import {
  type AcquireMultipleBookHandoffData,
  type BookTypeEng,
} from "@/types/acquirebook";
import { AcquireBookModal } from "../components/AcquireBookModal";
import type { ISingleBookAcquireDialogData } from "@/types/singleBookAcquireDialog";
import { CustomModal } from "@/components/Modal";

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  apiClient: AxiosInstance;
  monitoringId: number | null;
}

/**
 * モニタリング結果モーダル
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const MonitoringResultModal: React.FC<Props & DialogProps> = (props) => {
  const { open, setOpen, onClose, apiClient, monitoringId } = props;
  const navigate = useNavigate();
  const { handleSubmit: acquireBookFormHandleSubmit } = useAcquireBookForm();

  // 検索項目
  const [searchShozai, setSearchShozai] = useState<string>(""); // 所在地
  const [searchChibanKaokuNumber, setSearchChibanKaokuNumber] =
    useState<string>(""); // 地番または家屋番号
  const [searchReceptionReason, setSearchReceptionReason] =
    useState<string>("0"); // 登記原因
  const [searchRealEstateType, setSearchRealEstateType] = useState<string>("0"); // 不動産種別

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [receptionReason, setReceptionReason] = useState<
    Array<{ label: string; value: string }>
  >([]); // 登記原因
  const [realEstateType, setRealEstateType] = useState<
    Array<{ label: string; value: string }>
  >([]); // 不動産種別
  const [defaultRows, setDefaultRows] = useState<MonitoringResultDataGridRow[]>(
    []
  ); // モニタリング結果用State
  const [displayRows, setDisplayRows] = useState<MonitoringResultDataGridRow[]>(
    []
  ); // モニタリング結果表示用State
  const [openAcquireMultipleBooksModal, setOpenAcquireMultipleBooksModal] =
    useState<boolean>(false); // 登記図面取得モーダル表示制御用のState
  const [acquireMultipleBooksRows, setAcquireMultipleBooksRows] = useState<
    AcquireMultipleBookHandoffData[]
  >([]); // 登記図面取得モーダル用のState
  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>([]); // チェックボックスやAction選択の管理用State
  const [acquireBookOpen, setAcquireBookOpen] = useState<boolean>(false); // 不動産登記取得モーダル表示用State
  const [prefectures, setPrefectures] = useState<IPrefecture[]>([]);
  const [
    disabledAcquireMultipleBooksButton,
    setDisabledAcquireMultipleBooksButton,
  ] = useState<boolean>(false); // 登記一括取得ボタンの活性非活性

  // 結果のチェックボックスのON/OFF時に実行される。
  const handleRowSelectionChange = (
    newSelectionModel: GridSelectionModel
  ): void => {
    setSelectedRowIds(newSelectionModel);
  };

  /**
   * 各種別でチェックボックスの活性非活性を判定。
   * /features/acquireMultipleBooks/presenter.tsxのcheckDisable()を参考にした。
   * @param {KeyName} keyName
   * @param {BookTypeEng} realEstateType
   * @returns {boolean}
   */
  const checkDisable = (keyName: KeyName, realEstateType: string): boolean => {
    const table: Record<string, Record<string, boolean>> = {
      syoyusya: { 土地: false, 建物: false, 区分建物: false },
      zennbu: { 土地: false, 建物: false, 区分建物: false },
      sintaku: { 土地: false, 建物: false, 区分建物: false },
      kyodo: { 土地: false, 建物: false, 区分建物: false },
      genzai: { 土地: false, 建物: false, 区分建物: false },
      tiseki: { 土地: false, 建物: true, 区分建物: true },
      densi: { 土地: false, 建物: true, 区分建物: true },
      tieki: { 土地: false, 建物: true, 区分建物: true },
      tatemono: { 土地: true, 建物: false, 区分建物: false },
    };

    return table[keyName][realEstateType] ?? false;
  };

  /**
   * resultsに「登記取得する」時に表示するチェックボックスの初期値を設定。
   * @param {MonitoringDetailEntity[]} details
   * @returns {MonitoringResultDataGridRow[]}
   */
  const initialize = (
    details: MonitoringDetailEntity[]
  ): MonitoringResultDataGridRow[] => {
    return details.map((detail) => {
      const gridRow: MonitoringResultDataGridRow = {
        id: detail.id,
        prefectureName:
          prefectures.find(
            (prefecture) => prefecture.id === detail.prefectureId
          )?.name ?? "",
        realEstateType: detail.realEstateType,
        location: detail.location,
        chibanKaokuNumber: detail.chibanKaokuNumber,
        registrationDate: detail.registrationDate,
        remarks: detail.remarks,
        syoyusya: {
          checked: false,
          unchecked: checkDisable("syoyusya", detail.realEstateType),
        },
        zennbu: {
          checked: false,
          unchecked: checkDisable("zennbu", detail.realEstateType),
        },
        sintaku: {
          checked: false,
          unchecked: checkDisable("sintaku", detail.realEstateType),
        },
        kyodo: {
          checked: false,
          unchecked: checkDisable("kyodo", detail.realEstateType),
        },
        genzai: {
          checked: false,
          unchecked: checkDisable("genzai", detail.realEstateType),
        },
        tiseki: {
          checked: false,
          unchecked: checkDisable("tiseki", detail.realEstateType),
        },
        densi: {
          checked: false,
          unchecked: checkDisable("densi", detail.realEstateType),
        },
        tieki: {
          checked: false,
          unchecked: checkDisable("tieki", detail.realEstateType),
        },
        tatemono: {
          checked: false,
          unchecked: checkDisable("tatemono", detail.realEstateType),
        },
      };
      if (detail.monitoringResult) {
        gridRow.requestDate =
          detail.monitoringResult.legalAffairsBureauRequestDate;
        gridRow.receptionReasonId = detail.monitoringResult.receptionReason?.id;
        gridRow.receptionReason =
          detail.monitoringResult.receptionReason?.receptionReasonFormal;
        gridRow.outside = detail.monitoringResult.outside;
      }
      return gridRow;
    });
  };

  // CSVダウンロード処理
  const handleCsvDownloadButtonClick = (): void => {
    const gridRowId: GridRowId[] = [Number(monitoringId)];
    GetMonitoringAsFileAPI(apiClient, { ids: gridRowId }).catch((error) => {
      if (
        isAxiosError<AxiosResponse>(error) &&
        error.response?.status === HttpStatusCode.BadRequest
      ) {
        toast.error("CSVファイルのダウンロードに失敗しました。");
        return;
      }
      if (
        isAxiosError<AxiosResponse>(error) &&
        (error.response?.status ?? HttpStatusCode.InternalServerError) >=
          HttpStatusCode.InternalServerError
      ) {
        SendToTrackingService(error);
        toast.error("CSVファイルのダウンロードに失敗しました");
      }
    });
  };

  // (単一)登記情報取得モーダル
  const transferSingleBookAcquireDialogData = (
    rows: MonitoringResultDataGridRow[],
    selectedIds: GridSelectionModel
  ): ISingleBookAcquireDialogData => {
    const row = rows.find((row) => row.id === selectedIds[0]);
    if (!row) {
      return {
        prefecturesId: 0,
        prefectures: "",
        address: "",
        chiban: "",
        kaokuNumber: "",
        realEstateTypeEng: "",
      };
    }
    return {
      prefecturesId:
        prefectures.find((prefecture) => prefecture.name === row.prefectureName)
          ?.id ?? 0,
      prefectures: row.prefectureName,
      address: row.location,
      chiban: row.chibanKaokuNumber,
      kaokuNumber: "",
      realEstateTypeEng: getBookTypeFromRealEstateTypeLabel(row.realEstateType),
    };
  };

  // 登記一括取得ボタンを押下時の、不動産登記取得モーダルの表示処理
  const handleClickAcquireButton = (): void => {
    if (selectedRowIds.length === 0) {
      toast.info("取得対象が選択されていません");
      return;
    }
    const acquireMultipleBooksRows = [];
    for (const [index, id] of selectedRowIds.entries()) {
      const monitoringResult = defaultRows.find((row) => row.id === id);
      if (!monitoringResult) {
        continue;
      }
      acquireMultipleBooksRows.push({
        id: index + 1,
        bookType: getBookTypeFromRealEstateTypeLabel(
          monitoringResult.realEstateType
        ),
        prefCode:
          prefectures.find(
            (prefecture) => prefecture.name === monitoringResult.prefectureName
          )?.prefCode ?? "",
        prefName: monitoringResult.prefectureName,
        locationFull: monitoringResult.location,
        chibanKaokuNumber: monitoringResult.chibanKaokuNumber,
        kyoutan: false,
        sintaku: false,
        genzai: false,
        ownerInfo: false,
        electricDrawings: false,
        tisekiDrawings: false,
        tiekiDrawings: false,
        buildingDrawings: false,
      });
    }
    setAcquireMultipleBooksRows(acquireMultipleBooksRows);
    setOpenAcquireMultipleBooksModal(true);
  };

  // データ取得処理
  useEffect(() => {
    const fetchMonitoringDetails = async (): Promise<
      GetMonitoringDetailsAPIResponse | undefined
    > => {
      try {
        const monitoringDetails =
          await apiClient.get<GetMonitoringDetailsAPIResponse>(
            `/monitoring/${String(monitoringId)}`,
            {
              params: {
                getMonitoringResult: true,
              },
            }
          );
        return monitoringDetails.data;
      } catch (error) {
        setOpen(false);
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Unauthorized
        ) {
          toast.info("再度該当のリンクを押下してください");
          return;
        }
        if (
          isAxiosError(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          toast.error("モニタリング結果の取得に失敗しました");
          SendToTrackingService(error);
        }
      }
    };

    (async () => {
      if (open && monitoringId) {
        // 初期化処理
        setSelectedRowIds([]);
        setAcquireBookOpen(false);
        setSearchShozai("");
        setSearchChibanKaokuNumber("");
        setSearchReceptionReason("0");
        setSearchRealEstateType("0");

        // 検索処理
        setIsLoading(true);
        const details = await fetchMonitoringDetails();
        const receptionReason = new Map<
          string,
          { label: string; value: string }
        >();
        for (const detail of details?.details ?? []) {
          if (detail.monitoringResult?.receptionReason) {
            receptionReason.set(
              String(detail.monitoringResult.receptionReason.id),
              {
                label:
                  detail.monitoringResult.receptionReason.receptionReasonFormal,
                value: String(detail.monitoringResult.receptionReason.id),
              }
            );
          }
        }
        setReceptionReason(
          Array.from(receptionReason.values()).sort((a, b) => {
            return Number(a.value) - Number(b.value);
          })
        );
        const realEstateTypeSet = new Set(
          details ? details.details.map((detail) => detail.realEstateType) : []
        );
        setRealEstateType(
          Array.from(realEstateTypeSet).map((realEstateType) => {
            return {
              label: realEstateType,
              value: realEstateType,
            };
          })
        );
        setDefaultRows(details ? initialize(details.details) : []);
        filterRows(details ? initialize(details.details) : []);
        setIsLoading(false);
      }
    })();
  }, [open, monitoringId, prefectures]);

  // 都道府県取得処理
  useEffect(() => {
    (async () => {
      // 都道府県リストをAPIから取得する。
      const prefectures = await GetPrefecturesAPI(apiClient);
      setPrefectures(prefectures);
    })();
  }, []);

  // 検索でのフィルタリング
  const filterRows = (_defaultRows?: MonitoringResultDataGridRow[]): void => {
    let displayRows = _defaultRows ?? defaultRows;
    if (searchShozai) {
      displayRows = displayRows.filter((row) => {
        return row.location.includes(searchShozai);
      });
    }
    if (searchChibanKaokuNumber) {
      displayRows = displayRows.filter((row) => {
        return row.chibanKaokuNumber.includes(searchChibanKaokuNumber);
      });
    }
    if (searchReceptionReason && searchReceptionReason !== "0") {
      displayRows = displayRows.filter((row) => {
        return row.receptionReasonId === Number(searchReceptionReason);
      });
    }
    if (searchRealEstateType && searchRealEstateType !== "0") {
      displayRows = displayRows.filter((row) => {
        return row.realEstateType === searchRealEstateType;
      });
    }
    setDisplayRows(displayRows);
  };

  // フィルタリング処理
  useEffect(() => {
    filterRows();
  }, [
    searchShozai,
    searchChibanKaokuNumber,
    searchReceptionReason,
    searchRealEstateType,
  ]);

  // 各ボタンの活性非活性制御
  useEffect(() => {
    if (selectedRowIds.length) {
      setDisabledAcquireMultipleBooksButton(false);
    } else {
      setDisabledAcquireMultipleBooksButton(true);
    }
  }, [selectedRowIds.length]);

  return (
    <CustomModal
      isOpen={open}
      handleClose={(event, reason) => {
        setOpen(false);
        if (onClose) {
          onClose(event, reason);
        }
      }}
      ariaLabelledby={"monitoring-result-modal"}
      ariaDescribedby={"monitoring-result-modal"}
      height={"90%"}
      width={"90%"}
      overflow={"scroll"}
    >
      <PageTitle>モニタリング結果</PageTitle>

      {/* 検索項目 */}
      <Box sx={{ mx: 2, mb: 2 }}>
        <Box width={"25%"} sx={{ display: "inline-block", top: "50%", mr: 1 }}>
          <Label>所在</Label>
          <TextField
            id="searchShozai"
            label="所在"
            value={searchShozai}
            fullWidth
            onChange={(event) => {
              setSearchShozai(event.target.value);
            }}
          />
        </Box>
        <Box width={"25%"} sx={{ display: "inline-block", top: "50%", mr: 1 }}>
          <Label>地番または家屋番号</Label>
          <TextField
            id="searchChibanKaokuNumber"
            label="地番または家屋番号"
            value={searchChibanKaokuNumber}
            fullWidth
            onChange={(event) => {
              setSearchChibanKaokuNumber(event.target.value);
            }}
          />
        </Box>
      </Box>
      <Box sx={{ mx: 2, mb: 2 }}>
        <Box width={"25%"} sx={{ display: "inline-block", top: "50%", mr: 1 }}>
          <Label>登記原因</Label>
          <Select
            value={searchReceptionReason}
            fullWidth
            onChange={(event: SelectChangeEvent<string>) => {
              setSearchReceptionReason(event.target.value);
            }}
          >
            <MenuItem key={`reception-reason-0`} value={"0"}>
              指定なし
            </MenuItem>
            {receptionReason.map((reason) => (
              <MenuItem
                key={`reception-reason-${reason.value}`}
                value={reason.value}
              >
                {reason.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box width={"25%"} sx={{ display: "inline-block", top: "50%", mr: 1 }}>
          <Label>不動産種別</Label>
          <Select
            value={searchRealEstateType}
            fullWidth
            onChange={(event: SelectChangeEvent<string>) => {
              setSearchRealEstateType(event.target.value);
            }}
          >
            <MenuItem key={`real-estate-type-0`} value={"0"}>
              指定なし
            </MenuItem>
            {realEstateType.map((ret) => (
              <MenuItem key={`real-estate-type-${ret.value}`} value={ret.value}>
                {ret.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* 結果 */}
      <Box sx={{ mt: 3 }}>
        {isLoading ? (
          <Stack alignItems="center">
            <CircularProgress sx={{ my: "20px" }} />
          </Stack>
        ) : (
          <>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button
                label="CSVダウンロード"
                onClick={handleCsvDownloadButtonClick}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.MEDIUM}
              />
            </Box>
            <MonitoringResultDataGrid
              handleRowSelectionChange={handleRowSelectionChange}
              navigate={navigate}
              rows={displayRows}
              setSelectedIds={setSelectedRowIds}
              setAcquireBookOpen={setAcquireBookOpen}
              disabledAcquireMultipleBooksButton={
                disabledAcquireMultipleBooksButton
              }
              handleClickAcquireButton={handleClickAcquireButton}
            />
          </>
        )}
      </Box>

      {/* (単一)登記取得モーダル */}
      <AcquireBookModal
        acquireBookOpen={acquireBookOpen}
        handleClose={() => {
          setAcquireBookOpen(false);
          setSelectedRowIds([]);
        }}
        row={transferSingleBookAcquireDialogData(defaultRows, selectedRowIds)}
      />
      {/* 一括登記取得モーダル */}
      <Modal
        open={openAcquireMultipleBooksModal}
        aria-labelledby={"acquire-multiple-books-modal"}
        aria-describedby={"acquire-multiple-books-modal"}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            outline: "none",
          }}
          onClick={() => {
            setOpenAcquireMultipleBooksModal(false);
          }}
        >
          <Box
            sx={{
              width: 1600,
              maxWidth: "90%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <AcquireMultipleBooksModal
              handleSubmit={acquireBookFormHandleSubmit}
              rows={acquireMultipleBooksRows}
            />
          </Box>
        </Box>
      </Modal>
    </CustomModal>
  );
};
